import { useEffect, useState } from 'react';

/** Delay applied when a caller does not pick one. */
export const DEFAULT_DEBOUNCE_DELAY_MS = 300;

/**
 * Mirrors `value` after it has held steady for `delayMs`.
 *
 * `fleet-management` has a lodash-backed `useDebounce`, but it is
 * app-local and this package cannot import across that boundary, so
 * the same behaviour is rebuilt here on a plain timer.
 *
 * @param value - Value to trail.
 * @param delayMs - Quiet period a value must survive before it is emitted.
 * @returns The last value that stayed unchanged for the full delay.
 */
export const useDebouncedValue = <T>(
  value: T,
  delayMs: number = DEFAULT_DEBOUNCE_DELAY_MS
): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delayMs]);

  return debouncedValue;
};

export default useDebouncedValue;
